/**
 * Admin Authentication Context
 *
 * Manages admin panel authentication state and operations.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/adminApi';
import { toast } from 'sonner';

const AdminAuthContext = createContext({});

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isBackupAuth, setIsBackupAuth] = useState(false);
  const navigate = useNavigate();

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const storedUser = localStorage.getItem('admin_user');

        if (token && storedUser) {
          // Verify token is still valid
          try {
            const userData = await authApi.getMe();
            setUser(userData);
            setIsBackupAuth(userData.is_backup_auth || false);
          } catch (error) {
            // Token invalid, clear storage
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
          }
        }
      } catch (error) {
        console.error('Failed to load user:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(async (email, password) => {
    try {
      const response = await authApi.login(email, password);

      localStorage.setItem('admin_token', response.access_token);
      localStorage.setItem('admin_user', JSON.stringify(response.admin_user));

      setUser(response.admin_user);
      setIsBackupAuth(false);

      toast.success('Logged in successfully');
      navigate('/dashboard');

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate]);

  /**
   * Emergency backup login
   */
  const backupLogin = useCallback(async (username, password) => {
    try {
      const response = await authApi.backupLogin(username, password);

      // Full super_admin permissions (must match ROLE_PERMISSIONS in admin_auth.py)
      const superAdminPermissions = [
        "view_dashboards",
        "view_users_list",
        "view_user_details",
        "suspend_user",
        "unsuspend_user",
        "adjust_credits",
        "view_config",
        "edit_config",
        "delete_config",
        "view_security_config",
        "view_pending_employees",
        "approve_employees",
        "reject_employees",
        "assign_admin_role",
        "backup_auth",
        "view_audit_logs",
        "view_kb",
        "manage_kb",
        "view_mrr_arr",
        "view_churn_analytics",
        "view_feature_usage",
        "view_cohort_analytics",
        "view_unit_economics",
        "view_llm_costs",
        "view_credit_economics",
        "view_revenue_recognition",
        "view_profitability",
        "view_support_tools",
        "bulk_operations",
        "view_scheduled_jobs",
        "manage_scheduled_jobs",
      ];

      const backupUser = {
        email: username,
        role: 'super_admin',
        full_name: 'Backup Administrator',
        is_backup_auth: true,
        permissions: superAdminPermissions,
      };

      localStorage.setItem('admin_token', response.access_token);
      localStorage.setItem('admin_user', JSON.stringify(backupUser));

      setUser(backupUser);
      setIsBackupAuth(true);

      toast.warning(response.warning || 'Backup authentication used');
      navigate('/dashboard');

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Backup login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate]);

  /**
   * Signup for new employee
   */
  const signup = useCallback(async (data) => {
    try {
      const response = await authApi.signup(data);

      toast.success(response.message || 'Signup successful. Awaiting approval.');
      navigate('/login');

      return { success: true, data: response };
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate]);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      setUser(null);
      setIsBackupAuth(false);
      navigate('/login');
      toast.success('Logged out');
    }
  }, [navigate]);

  /**
   * Check if user has permission
   */
  const hasPermission = useCallback((permission) => {
    if (!user) return false;
    return user.permissions?.includes(permission) || false;
  }, [user]);

  /**
   * Check if user has role or higher
   *
   * Role hierarchy: viewer < pm < operator < admin < super_admin
   * PM is a specialized role with analytics access but no operational permissions.
   */
  const hasRole = useCallback((requiredRole) => {
    if (!user) return false;

    // Role hierarchy: viewer < pm < operator < admin < super_admin
    const roleHierarchy = ['viewer', 'pm', 'operator', 'admin', 'super_admin'];
    const userRoleIndex = roleHierarchy.indexOf(user.role);
    const requiredRoleIndex = roleHierarchy.indexOf(requiredRole);

    // Handle case where role is not in hierarchy
    if (userRoleIndex === -1 || requiredRoleIndex === -1) {
      return false;
    }

    return userRoleIndex >= requiredRoleIndex;
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isBackupAuth,
    login,
    backupLogin,
    signup,
    logout,
    hasPermission,
    hasRole,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
